<template>
  <div class="wrap">

    <!-- 탭 -->
    <div class="tabs">
      <div
        v-for="tab in tabs"
        :key="tab.value"
        :class="['tab', { active: selectedTab === tab.value }]"
        @click="selectedTab = tab.value"
      >{{ tab.label }}</div>
    </div>

    <!-- ══ 공통 1행: 4칸 센서 ══ -->
    <div class="row1">
      <div class="r1-cell">
        <div class="r1-label">현재 무게</div>
        <div class="r1-val">{{ status.weight.toLocaleString() }}<span class="r1-unit"> KG</span></div>
      </div>
      <div class="r1-cell">
        <div class="r1-label">부하율</div>
        <div class="r1-val warm">{{ status.buha }} A</div>
      </div>
      <div class="r1-cell">
        <div class="r1-label">바닥 온도</div>
        <div class="r1-val warm">{{ status.floorTemp }}℃</div>
      </div>
      <div class="r1-cell r1-cell--double">
        <div class="r1-label">내부 온도</div>
        <div class="r1-row">
          <span class="r1-val warm">{{ status.inTemp }}℃</span>
          <span class="r1-divider">/</span>
          <span class="r1-val warm">{{ status.inHumi }}℃</span>
        </div>
      </div>
    </div>

    <!-- ══ 공통 2행: 배합기/인덕션 % ══ -->
    <div class="row2">
      <div class="r2-half">
        <div class="r2-label">배합기 현재값</div>
        <div class="r2-bar-wrap">
          <div class="r2-bar blue" :style="{ width: status.baehapPct + '%' }"></div>
        </div>
        <div class="r2-val">{{ status.baehapPct }} %</div>
      </div>
      <div class="r2-half">
        <div class="r2-label">인덕션 현재값</div>
        <div class="r2-bar-wrap">
          <div class="r2-bar green" :style="{ width: status.inductionPct + '%' }"></div>
        </div>
        <div class="r2-val">{{ status.inductionPct }} %</div>
      </div>
    </div>

    <!-- ══ 탭별 3행: 상태/모드 ══ -->

    <!-- 배합기 -->
    <div v-if="selectedTab === 'baehapgi'" class="state-row">
      <div class="state-cell">
        <div class="state-label">상태</div>
        <span :class="['badge', isLocal ? 'local' : 'remote']">{{ isLocal ? 'Local' : 'Remote' }}</span>
      </div>
      <div class="state-cell">
        <div class="state-label">차도모드 선택</div>
        <button class="ghost-btn" @click="isLocal = !isLocal">사이클 모드</button>
      </div>
    </div>

    <!-- 천장+ -->
    <div v-if="selectedTab === 'ceiling'" class="state-row state-row--3">
      <div class="state-cell">
        <div class="state-label">천장/바스켓 모드</div>
        <span :class="['badge', isLocal ? 'local' : 'remote']">{{ isLocal ? 'Local' : 'Remote' }}</span>
      </div>
      <div class="state-cell">
        <div class="state-label">천장 상태</div>
        <span class="state-chip warn">{{ ceiling.ceilStatus }}</span>
      </div>
      <div class="state-cell">
        <div class="state-label">바스켓 상태</div>
        <span class="state-chip neutral">{{ ceiling.basketStatus }}</span>
      </div>
    </div>

    <!-- 컨베어+ -->
    <div v-if="selectedTab === 'conveyor'" class="state-row state-row--3">
      <div class="state-cell">
        <div class="state-label">컨베어/토출 모드</div>
        <span :class="['badge', isLocal ? 'local' : 'remote']">{{ isLocal ? 'Local' : 'Remote' }}</span>
      </div>
      <div class="state-cell">
        <div class="state-label">설정 (시간)</div>
        <span class="state-val">{{ conveyor.timeSec }} <em>Sec</em></span>
      </div>
      <div class="state-cell">
        <div class="state-label">설정 (무게)</div>
        <span class="state-val">{{ conveyor.weightKg }} <em>KG</em></span>
      </div>
    </div>

    <!-- ══ 배합기 탭 전용: 모니터링 + 레시피 버튼 ══ -->
    <template v-if="selectedTab === 'baehapgi'">
      <div class="monitor-grid">
        <div class="monitor-cell">
          <div class="monitor-label">발효 카운트</div>
          <div class="monitor-val">{{ status.fermentCount }}<span class="monitor-unit"> 회</span></div>
        </div>
        <div class="monitor-cell">
          <div class="monitor-label">발효 잔여</div>
          <div class="monitor-val">{{ status.fermentLeft }}<span class="monitor-unit"> 분</span></div>
        </div>
      </div>
      <div class="recipe-btn-row">
        <button class="recipe-btn" @click="$refs.recipeModal.show()">⚙ 배합 설정</button>
      </div>
    </template>

    <!-- ══ 컨트롤부 ══ -->
    <div class="devices">

      <!-- 배합기 -->
      <template v-if="selectedTab === 'baehapgi'">
        <div v-for="d in baehapgiDevices" :key="d.key" class="d-row">
          <div class="d-name">{{ d.name }}</div>
          <button :class="['d-btn', state[d.key] ? 'on' : 'dim']" @click="tap(d.key, true)">ON</button>
          <button :class="['d-btn', !state[d.key] ? 'off' : 'dim']" @click="tap(d.key, false)">OFF</button>
        </div>
      </template>

      <!-- 천장+ -->
      <template v-if="selectedTab === 'ceiling'">
        <div class="d-row">
          <div class="d-name">천장</div>
          <button :class="['d-btn', state.ceilOpen ? 'on' : 'dim']" @click="tap('ceilOpen', true)">열림</button>
          <button :class="['d-btn', !state.ceilOpen ? 'off' : 'dim']" @click="tap('ceilOpen', false)">닫힘</button>
        </div>
        <div class="d-row">
          <div class="d-name">바스켓</div>
          <button :class="['d-btn', state.basketUp ? 'on' : 'dim']" @click="tap('basketUp', true)">올림</button>
          <button :class="['d-btn', !state.basketUp ? 'off' : 'dim']" @click="tap('basketUp', false)">내림</button>
        </div>
        <div class="d-row">
          <div class="d-name">바스켓<br>자동모드</div>
          <button :class="['d-btn', state.basketAuto ? 'on' : 'dim']" @click="tap('basketAuto', true)">ON</button>
          <button :class="['d-btn', !state.basketAuto ? 'off' : 'dim']" @click="tap('basketAuto', false)">OFF</button>
        </div>
        <div class="d-row">
          <div class="d-name">바스켓<br>자동모드2</div>
          <button :class="['d-btn', state.basketAuto2 ? 'on' : 'dim']" @click="tap('basketAuto2', true)">ON</button>
          <button :class="['d-btn', !state.basketAuto2 ? 'off' : 'dim']" @click="tap('basketAuto2', false)">OFF</button>
        </div>
      </template>

      <!-- 컨베어+ -->
      <template v-if="selectedTab === 'conveyor'">
        <div class="d-row">
          <div class="d-name">컨베아어</div>
          <button :class="['d-btn', state.convUp ? 'on' : 'dim']" @click="tap('convUp', true)">올림</button>
          <button :class="['d-btn', !state.convUp ? 'off' : 'dim']" @click="tap('convUp', false)">내림</button>
        </div>
        <div class="d-row">
          <div class="d-name">토출</div>
          <button :class="['d-btn', state.discharge ? 'on' : 'dim']" @click="tap('discharge', true)">ON</button>
          <button :class="['d-btn', !state.discharge ? 'off' : 'dim']" @click="tap('discharge', false)">OFF</button>
        </div>
        <div class="d-row">
          <div class="d-name">토출문</div>
          <button :class="['d-btn', state.dischargeGate ? 'on' : 'dim']" @click="tap('dischargeGate', true)">열림</button>
          <button :class="['d-btn', !state.dischargeGate ? 'off' : 'dim']" @click="tap('dischargeGate', false)">닫힘</button>
        </div>
        <div class="d-row">
          <div class="d-name">토출<br>자동모드</div>
          <button :class="['d-btn', state.dischargeAuto ? 'on' : 'dim']" @click="tap('dischargeAuto', true)">ON</button>
          <button :class="['d-btn', !state.dischargeAuto ? 'off' : 'dim']" @click="tap('dischargeAuto', false)">OFF</button>
        </div>
      </template>

    </div>

    <!-- ══ 배합 레시피 모달 ══ -->
    <b-modal
      ref="recipeModal"
      title="배합 설정"
      ok-title="설정 적용"
      cancel-title="취소"
      :ok-disabled="totalPct !== 100"
      centered
      scrollable
      @ok="onRecipeApply"
    >
			<div class="d-flex justify-content-around mb-1 p-75 rounded" style="background:#f5f5f5">
				<div class="text-center">
					<div class="modal-section-label mb-25">총 배합량</div>
					<strong>{{ monitor.totalMix.toLocaleString() }}</strong> <small class="text-muted">KG</small>
				</div>
				<div class="text-center">
					<div class="modal-section-label mb-25">배합 시간</div>
					<strong>{{ monitor.mixTime }}</strong> <small class="text-muted">분</small>
				</div>
			</div>
      <!-- 배합사료코드 -->
      <p class="modal-section-label">배합사료코드</p>
      <div v-for="(item, i) in recipe" :key="'code-'+i" class="modal-row">
        <span class="modal-row-num">{{ i + 1 }}번</span>
        <b-form-select v-model="item.code" :options="feedSelectOptions" size="sm" />
      </div>

      <hr class="my-1">

      <!-- 배합비 -->
      <div class="d-flex justify-content-between align-items-center mb-1">
        <p class="modal-section-label mb-0">배합비 설정</p>
        <b-badge :variant="totalPct === 100 ? 'success' : 'danger'">합계 {{ totalPct }}%</b-badge>
      </div>
      <div v-for="(item, i) in recipe" :key="'pct-'+i" class="modal-row mb-75">
        <span class="modal-row-num">{{ i + 1 }}번</span>
        <b-form-input
          v-model.number="item.pct"
          type="number"
          min="0"
          max="100"
          :disabled="!item.code"
          size="sm"
          style="width: 70px; text-align: center;"
        />
        <span class="modal-pct-unit">%</span>
        <div class="modal-pct-bar-wrap">
          <div class="modal-pct-bar" :style="{ width: Math.min(item.pct || 0, 100) + '%' }"></div>
        </div>
      </div>
      <b-alert :show="totalPct !== 100" variant="danger" class="mt-1 py-75 px-1">
        <small>⚠ 합계가 100%가 되어야 합니다. (현재 {{ totalPct }}%)</small>
      </b-alert>
    </b-modal>

  </div>
</template>

<script>
import {
  BModal, BFormSelect, BFormInput, BBadge, BAlert,
} from 'bootstrap-vue'

export default {
  name: 'BaehapgiControl',
  components: {
    BModal, BFormSelect, BFormInput, BBadge, BAlert,
  },
  props: {
    buttonItem: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      selectedTab: 'baehapgi',
      isLocal: true,
      tabs: [
        { label: '배합기',  value: 'baehapgi' },
        { label: '천장+',   value: 'ceiling'  },
        { label: '컨베어+', value: 'conveyor' },
      ],
      baehapgiDevices: [
        { key: 'baehapgi',    name: '배합기'        },
        { key: 'induction',   name: '인덕션'        },
        { key: 'autoMode',    name: '배합\n자동모드' },
        { key: 'fermentMode', name: '자동\n발효모드' },
      ],
      feedOptions: [
        { code: '2171800', name: '사일리지(섬유질발효사료)'},
        { code: '506069', name: '단백피'            },
        { code: '2111400', name: '혼합곡물' },
        { code: '2150600', name: '비지(콩류가공부산물)'            },
        { code: '506003', name: '물(수분)'     },
      ],
      recipe: [
        { code: '2171800', pct: 40 },
        { code: '506069', pct: 20 },
        { code: '2111400', pct: 20 },
        { code: '2150600', pct: 10  },
        { code: '506003', pct: 10  },
      ],
      state: {
        baehapgi: false, induction: false, autoMode: false, fermentMode: false,
        ceilOpen: false, basketUp: false, basketAuto: false, basketAuto2: false,
        convUp: false, discharge: false, dischargeGate: false, dischargeAuto: false,
      },
      status: {
        weight: 12346, buha: 0, floorTemp: 25, inTemp: 28, inHumi: 25,
        baehapPct: 0, inductionPct: 0,
        fermentCount: 12, fermentLeft: 12,
      },
      monitor: {
        totalMix: 12500,
        mixTime: 45,
      },
      ceiling: { ceilStatus: 'CLOSE', basketStatus: '정지' },
      conveyor: { timeSec: 12, weightKg: 12 },
    }
  },
  computed: {
    totalPct() {
      return this.recipe.reduce((sum, r) => sum + (Number(r.pct) || 0), 0)
    },
    feedSelectOptions() {
      return [
        { value: '', text: '선택안함' },
        ...this.feedOptions.map(f => ({ value: f.code, text: `${f.name}` })),
      ]
    },
  },
  methods: {
    tap(key, val) {
      this.state[key] = val
      // this.$emit('control', { key, val })
    },
    onRecipeApply() {
      // this.$emit('recipe', this.recipe)
      // this.socket.emit('baehapgi_recipe', this.recipe)
    },
  },
}
</script>

<style scoped>
* { box-sizing: border-box; margin: 0; padding: 0; }
.wrap {
  max-width: 390px; margin: 0 auto;
  background: #fff; border: 1px solid #e0e0e0;
  border-radius: 12px; overflow: hidden; font-family: inherit;
}
.tabs { display: flex; border-bottom: 1px solid #e0e0e0; }
.tab {
  flex: 1; padding: 11px 0; text-align: center;
  font-size: 15px; font-weight: 500; cursor: pointer;
  color: #999; background: #f5f5f5; border-right: 1px solid #e0e0e0;
}
.tab:last-child { border-right: none; }
.tab.active { background: #fff; color: #222; border-bottom: 2px solid #378ADD; }
.row1 { display: grid; grid-template-columns: 1fr 1fr 1fr 1.4fr; border-bottom: 1px solid #e0e0e0; }
.r1-cell { padding: 8px 6px; text-align: center; border-right: 1px solid #e0e0e0; }
.r1-cell:last-child { border-right: none; }
.r1-label { font-size: 10px; color: #bbb; margin-bottom: 3px; }
.r1-val { font-size: 14px; font-weight: 500; color: #222; }
.r1-val.warm { color: #D85A30; }
.r1-val.cool { color: #185FA5; }
.r1-unit { font-size: 10px; color: #999; }
.r1-cell--double .r1-row { display: flex; align-items: center; justify-content: center; gap: 4px; }
.r1-divider { font-size: 12px; color: #ccc; }
.row2 { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #e0e0e0; }
.r2-half { padding: 12px; text-align: center; }
.r2-half + .r2-half { border-left: 1px solid #e0e0e0; }
.r2-label { font-size: 11px; color: #bbb; margin-bottom: 6px; }
.r2-bar-wrap { background: #eee; border-radius: 8px; height: 5px; overflow: hidden; margin-bottom: 8px; }
.r2-bar { height: 100%; border-radius: 8px; }
.r2-bar.blue  { background: #378ADD; }
.r2-bar.green { background: #1D9E75; }
.r2-val { font-size: 32px; font-weight: 500; color: #222; line-height: 1; }
.state-row {
  display: grid; grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid #e0e0e0; background: #fafafa;
}
.state-row--3 { grid-template-columns: 1fr 1fr 1fr; }
.state-cell { padding: 8px 10px; text-align: center; border-right: 1px solid #e0e0e0; }
.state-cell:last-child { border-right: none; }
.state-label { font-size: 10px; color: #bbb; margin-bottom: 5px; }
.badge { font-size: 12px; padding: 3px 10px; border-radius: 20px; font-weight: 500; display: inline-block; }
.badge.local  { background: #EAF3DE; color: #3B6D11; }
.badge.remote { background: #E6F1FB; color: #185FA5; }
.ghost-btn {
  font-size: 12px; color: #888; background: #fff;
  border: 1px solid #ddd; border-radius: 8px; padding: 4px 10px; cursor: pointer;
}
.state-chip { font-size: 13px; font-weight: 500; }
.state-chip.warn    { color: #D85A30; }
.state-chip.neutral { color: #555; }
.state-val { font-size: 18px; font-weight: 500; color: #222; }
.state-val em { font-size: 12px; color: #999; font-style: normal; }
.monitor-grid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #e0e0e0; }
.monitor-cell { padding: 10px 12px; text-align: center; border-right: 1px solid #e0e0e0; }
.monitor-cell:nth-child(2n) { border-right: none; }
.monitor-cell:nth-child(n+3) { border-top: 1px solid #e0e0e0; }
.monitor-label { font-size: 10px; color: #bbb; margin-bottom: 4px; }
.monitor-val   { font-size: 20px; font-weight: 500; color: #222; }
.monitor-unit  { font-size: 12px; color: #999; }
.recipe-btn-row { padding: 10px 12px; border-bottom: 1px solid #e0e0e0; background: #fafafa; }
.recipe-btn {
  width: 100%; padding: 10px 0;
  background: #fff; color: #378ADD;
  border: 1px solid #378ADD; border-radius: 8px;
  font-size: 15px; font-weight: 500; cursor: pointer;
}
.recipe-btn:active { background: #f0f7ff; }
.devices { padding: 12px; }
.d-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.d-name {
  width: 54px; font-size: 13px; font-weight: 500;
  color: #222; text-align: center; white-space: pre-line; line-height: 1.3; flex-shrink: 0;
}
.d-btn {
  flex: 1; padding: 14px 0; border-radius: 50px;
  font-size: 16px; font-weight: 500; cursor: pointer;
  border: none; transition: background 0.15s;
}
.d-btn.on  { background: #378ADD; color: #fff; }
.d-btn.off { background: #E24B4A; color: #fff; }
.d-btn.dim { background: #eeeeee; color: #aaaaaa; }

/* 모달 내부 스타일 */
.modal-section-label { font-size: 12px; font-weight: 600; color: #888; margin-bottom: 8px; }
.modal-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.modal-row-num { font-size: 13px; color: #bbb; width: 28px; flex-shrink: 0; }
.modal-pct-unit { font-size: 13px; color: #999; flex-shrink: 0; }
.modal-pct-bar-wrap { flex: 1; height: 6px; background: #eee; border-radius: 6px; overflow: hidden; }
.modal-pct-bar { height: 100%; background: #378ADD; border-radius: 6px; transition: width 0.2s; }
.mb-75 { margin-bottom: 0.75rem; }
</style>
